import { useCallback, useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataGrid } from '../../components/DataGrid.js';
import { Button } from '../../../../shared/components/index.js';

// L5.01 — Master Data: Lookup (code-master) Catalogue.
// As-built: M05 exposes code/reference masters as "lookup categories"
// (T066) each holding "lookup values" (T067). One generic catalogue serves
// L5.01 (catalogue), and the detail page serves L5.02 (values), L5.03
// (reference viewer) and L5.05 (UoM/currency are themselves lookup categories).
// Backend: GET/POST /v1/master/lookup-categories,
// PATCH /v1/master/lookup-categories/:id/deactivate.

interface LookupCategory {
  readonly lookupCategoryId: string;
  readonly lookupCategoryCode: string;
  readonly categoryName: string;
  readonly description: string | null;
  readonly isSystemCategory: boolean;
  readonly recordStatusCode: string;
}

export function MasterLookupListPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<LookupCategory[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<LookupCategory[]>('/master/lookup-categories', { token });
      setRows(Array.isArray(r) ? r : []);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const onCreate = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiRequest('/master/lookup-categories', {
        method: 'POST', token,
        body: { lookupCategoryCode: code.trim(), categoryName: name.trim(), description: description.trim() || undefined },
      });
      setCode(''); setName(''); setDescription('');
      await load();
    } catch (err) { setFormError((err as Error).message); }
    finally { setSaving(false); }
  }, [code, name, description, token, load]);

  return (
    <main aria-label="master-lookup-catalogue">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('mdm.lookup.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>
        {t('mdm.lookup.intro')}
      </p>

      <form onSubmit={onCreate} aria-label="create-category-form"
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="cat-code" style={{ fontSize: 12 }}>{t('mdm.lookup.catCode')}</label>
          <input id="cat-code" value={code} onChange={(e) => setCode(e.target.value)} required
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="cat-name" style={{ fontSize: 12 }}>{t('mdm.lookup.displayName')}</label>
          <input id="cat-name" value={name} onChange={(e) => setName(e.target.value)} required
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label htmlFor="cat-desc" style={{ fontSize: 12 }}>{t('mdm.lookup.description')}</label>
          <input id="cat-desc" value={description} onChange={(e) => setDescription(e.target.value)}
                 style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
        </div>
        <Button type="submit" variant="primary" data-testid="create-category" disabled={saving}
                style={{ padding: '6px 14px', borderRadius: 4 }}>
          {saving ? t('mdm.common.adding') : t('mdm.lookup.addCategory')}
        </Button>
      </form>
      {formError && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{formError}</p>}

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="categories-empty" style={{ color: 'var(--color-text-muted)' }}>{t('mdm.lookup.empty')}</p>
      ) : (
        <DataGrid<any>
          caption="categories-table"
          rows={rows}
          getRowId={(c) => c.lookupCategoryId}
          rowTestId={(c) => `category-row-${c.lookupCategoryId}`}
          columns={[
            { key: 'code', header: t('mdm.col.code'), value: (c) => c.lookupCategoryCode, render: (c) => <Link to={`/admin/mdm/lookups/${c.lookupCategoryId}`}>{c.lookupCategoryCode}</Link> },
            { key: 'name', header: t('mdm.col.name'), value: (c) => c.categoryName },
            { key: 'type', header: t('mdm.col.type'), value: (c) => (c.isSystemCategory ? t('mdm.lookup.system') : t('mdm.lookup.custom')) },
            { key: 'status', header: t('mdm.col.status'), value: (c) => c.recordStatusCode },
          ]}
        />
      )}
    </main>
  );
}
